/**
 * Step 9b — Lightweight navigator handoff session.
 *
 * Tracks the most recent external-navigator launch from LTCC so that when
 * the user returns to the app we can show a smart "Tillbaka från navigator"
 * prompt. Pure runtime/session state — never persisted to the database, and
 * deliberately ignorant of what the navigator actually did.
 *
 * IMPORTANT: this must never be used to claim Google Maps or Waze "confirmed
 * arrival". It only knows that a handoff happened, not what came of it.
 */

export type NavigatorProvider = "google_maps" | "waze";

export interface NavigatorHandoffEvent {
  provider: NavigatorProvider;
  /** Trip the handoff was launched from. */
  tripId: string;
  /** Active stop index at launch — used to detect "still same leg on return". */
  activeIndex: number | null;
  /** Wall-clock ms when the handoff was triggered. */
  startedAt: number;
}

let current: NavigatorHandoffEvent | null = null;
const listeners = new Set<(e: NavigatorHandoffEvent | null) => void>();

function emit() {
  for (const l of listeners) l(current);
}

export function noteHandoff(event: Omit<NavigatorHandoffEvent, "startedAt">) {
  current = { ...event, startedAt: Date.now() };
  emit();
}

export function clearHandoff() {
  if (current === null) return;
  current = null;
  emit();
}

export function getCurrentHandoff(): NavigatorHandoffEvent | null {
  return current;
}

export function subscribeHandoff(
  listener: (e: NavigatorHandoffEvent | null) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}